import React from "react";
import { useNavigate } from "react-router-dom";
import { Download, Printer, Plus, SearchX, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import {
  BackLink,
  HeroStatus,
  Section,
  SectionLabel,
  EntityCard,
  StatCard,
  StatsRow,
  DetailList,
  FooterActions,
  ActionButton,
  TechDetailsToggle,
  EmptyState,
  KvGrid,
  KvItem,
  Card,
  CardTitle,
  fmtItalianDateTime,
  orDash,
  upperOrDash,
  EM_DASH,
} from "./Primitives";

/* ============================================================
 * Defensive accessors per shape PG OpenAPI.it
 *  - soggetti: [{denominazione, cf, piva, sede, catasti: [{provincia, citta, fabbricati, terreni}]}]
 *  - fallback su immobili[]/fabbricati[]/unita_immobiliari[] (shape generica)
 * ============================================================ */

const pickFirstSubject = (rd) => {
  if (!rd || typeof rd !== "object") return {};
  const list = Array.isArray(rd.soggetti) ? rd.soggetti : null;
  const s = (list && list[0]) || rd.soggetto || rd.persona || rd.dati || rd;
  return {
    denominazione: s?.denominazione || s?.ragione_sociale || s?.nome_completo || s?.nome,
    cf: s?.cf || s?.codice_fiscale || s?.cf_piva,
    piva: s?.piva || s?.partita_iva || s?.p_iva || s?.cf_piva,
    sede: s?.sede || s?.sede_legale || s?.indirizzo_sede,
    sede_comune: s?.sede_comune || s?.comune_sede,
    sede_provincia: s?.sede_provincia || s?.provincia_sede,
    catasti: Array.isArray(s?.catasti) ? s.catasti : null,
  };
};

const aggregateByProvincia = (subject, rd) => {
  // Caso A (preferito): subject.catasti pre-aggregati per provincia
  if (subject?.catasti && subject.catasti.length > 0) {
    return subject.catasti
      .map((c) => ({
        provincia: c?.provincia || c?.sigla_provincia || EM_DASH,
        citta: c?.citta || c?.comune || c?.localita || "",
        fabbricati: Number(c?.fabbricati || 0),
        terreni: Number(c?.terreni || 0),
      }))
      .filter((c) => c.fabbricati > 0 || c.terreni > 0)
      .sort((a, b) => b.fabbricati - a.fabbricati);
  }
  // Caso B (fallback): lista immobili da aggregare
  const items =
    rd?.immobili || rd?.fabbricati || rd?.unita || rd?.unita_immobiliari || rd?.beni || rd?.lista_immobili || [];
  if (!Array.isArray(items)) return [];
  const map = new Map();
  for (const it of items) {
    const prov = it?.provincia || it?.sigla_provincia || it?.prov || EM_DASH;
    if (!map.has(prov)) map.set(prov, { provincia: prov, citta: it?.comune || "", fabbricati: 0, terreni: 0 });
    map.get(prov).fabbricati += 1;
  }
  return Array.from(map.values()).sort((a, b) => b.fabbricati - a.fabbricati);
};

const buildSedeSub = (subject) => {
  if (subject?.sede) return `Sede legale: ${subject.sede}`;
  if (subject?.sede_comune) {
    const prov = subject.sede_provincia ? ` (${subject.sede_provincia})` : "";
    return `Sede legale: ${subject.sede_comune}${prov}`;
  }
  return null;
};

/* ============================================================
 * ResultCatastoPG
 * ============================================================ */

export const ResultCatastoPG = ({ request, onNewSearch }) => {
  const navigate = useNavigate();
  const completedAt = request?.completed_at || request?.created_at;
  const rd = request?.response_data || {};
  const subject = pickFirstSubject(rd);
  const breakdown = aggregateByProvincia(subject, rd);

  const totalFabbricati = breakdown.reduce((sum, b) => sum + (b.fabbricati || 0), 0);
  const provinceCount = breakdown.length;
  const provinceDetail =
    provinceCount === 0
      ? null
      : provinceCount === 1
      ? `a ${breakdown[0].citta ? breakdown[0].citta : breakdown[0].provincia}`
      : `in ${provinceCount} province`;

  const denominazione =
    subject?.denominazione || rd?.ragione_sociale || rd?.denominazione || EM_DASH;
  const pivaDisplay = subject?.piva
    ? `P.IVA ${upperOrDash(subject.piva)}`
    : subject?.cf
    ? `C.F. ${upperOrDash(subject.cf)}`
    : null;

  const onExportJson = () => {
    try {
      const payload = {
        request_id: request?.id,
        service_id: request?.service_id,
        completed_at: completedAt,
        params: request?.params,
        response_data: rd,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catasto_pg_${request?.id || "result"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export JSON");
    }
  };

  const onPrint = () => window.print();

  const onDownloadPdf = async () => {
    if (!request?.id) return;
    try {
      const token = localStorage.getItem("wattline_token");
      const res = await fetch(`${API}/requests/${request.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error("Errore download PDF"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verifica_catastale_${request.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download PDF"); }
  };

  const handleNewSearch = () => (onNewSearch ? onNewSearch() : navigate("/search/catasto"));

  const totalTerreni = 0; // ricerca limitata ai fabbricati
  const isEmpty = totalFabbricati === 0 && totalTerreni === 0;

  /* ----------------------- EMPTY STATE ----------------------- */
  if (isEmpty) {
    const pivaParam = request?.params?.piva || subject?.piva || subject?.cf || EM_DASH;
    return (
      <div className="max-w-3xl" data-testid="result-catasto-pg-empty">
        <BackLink to="/history" label="Storico richieste" />

        <HeroStatus
          variant="warning"
          title="Nessun immobile intestato"
          description={`La ricerca è andata a buon fine, ma il soggetto non ha fabbricati nel catasto · ${fmtItalianDateTime(completedAt)}`}
          testId="result-hero-empty"
        />

        <Section>
          <EmptyState
            icon={SearchX}
            title="Nessun fabbricato trovato"
            description={
              <>
                Per <strong className="text-text-primary">P.IVA {upperOrDash(pivaParam)}</strong> non risultano fabbricati intestati a livello nazionale al Catasto Fabbricati.
              </>
            }
            primaryAction={{ label: "Nuova ricerca", onClick: handleNewSearch }}
          />
        </Section>

        <Section>
          <Card>
            <CardTitle>Riepilogo ricerca</CardTitle>
            <KvGrid columns={2}>
              <KvItem label="P.IVA cercata" value={upperOrDash(pivaParam)} mono testId="kv-piva" />
              <KvItem label="Tipo catasto" value="Fabbricati" testId="kv-tipo" />
              <KvItem label="Ambito" value="Nazionale" testId="kv-ambito" />
              <KvItem label="Data ricerca" value={fmtItalianDateTime(completedAt)} testId="kv-data" />
            </KvGrid>
          </Card>
        </Section>

        <FooterActions>
          <ActionButton variant="primary" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
            Nuova ricerca
          </ActionButton>
          <ActionButton variant="outline" icon={RefreshCw} onClick={handleNewSearch} testId="action-retry">
            Riprova con stessa P.IVA
          </ActionButton>
        </FooterActions>

        <TechDetailsToggle data={request} />
      </div>
    );
  }

  /* ----------------------- SUCCESS STATE ----------------------- */

  return (
    <div className="max-w-3xl" data-testid="result-catasto-pg">
      <BackLink to="/history" label="Storico richieste" />

      <HeroStatus
        variant="success"
        title="Ricerca completata"
        description={`Catasto Fabbricati nazionale · ${fmtItalianDateTime(completedAt)}`}
        testId="result-hero"
      />

      <Section>
        <EntityCard
          title="Soggetto identificato"
          entity={String(denominazione).toUpperCase()}
          meta={pivaDisplay}
          sub={buildSedeSub(subject)}
        />
      </Section>

      <Section>
        <SectionLabel>Patrimonio immobiliare</SectionLabel>
        <StatsRow>
          <StatCard
            label="Fabbricati"
            value={totalFabbricati}
            detail={provinceDetail}
            testId="stat-fabbricati"
          />
          <StatCard
            label="Terreni"
            value={0}
            detail="Ricerca limitata ai fabbricati"
            muted
            emptyDetail
            testId="stat-terreni"
          />
        </StatsRow>
      </Section>

      {breakdown.length > 0 && (
        <Section>
          <SectionLabel>Dettaglio per provincia</SectionLabel>
          <DetailList
            testId="detail-list-province"
            items={breakdown.map((b) => ({
              key: b.provincia,
              main: `${b.citta ? String(b.citta).replace(/\b\w/g, (c) => c.toUpperCase()) : "—"} (${b.provincia})`,
              sub: "Catasto fabbricati · territorio nazionale",
              count: `${b.fabbricati} ${b.fabbricati === 1 ? "fabbricato" : "fabbricati"}`,
            }))}
          />
        </Section>
      )}

      <FooterActions>
        <ActionButton variant="primary" icon={FileText} onClick={onDownloadPdf} testId="action-download-pdf">
          Scarica report PDF
        </ActionButton>
        <ActionButton variant="outline" icon={Plus} onClick={handleNewSearch} testId="action-new-search">
          Nuova ricerca
        </ActionButton>
        <ActionButton variant="outline" icon={Download} onClick={onExportJson} testId="action-export-json">
          Esporta JSON
        </ActionButton>
        <ActionButton variant="outline" icon={Printer} onClick={onPrint} testId="action-print">
          Stampa
        </ActionButton>
      </FooterActions>

      <TechDetailsToggle data={request} />
    </div>
  );
};
